import React, { useEffect, useRef, useState } from 'react';
import { ArrowLeft, Leaf, Sparkles, Check } from 'lucide-react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import BackToTop from '../components/BackToTop';

const COMMUNITY_IMAGE = 'https://customer-assets.emergentagent.com/job_3f9e40e3-cca9-4507-83e7-20ced098354a/artifacts/484wso74_COMMUNITY%20IMAGE.jpg';
const PEOPLE_IMAGE = 'https://customer-assets.emergentagent.com/job_3f9e40e3-cca9-4507-83e7-20ced098354a/artifacts/rbg3iph1_PEOPLE%20IMAGE.png';
const HERO_IMAGE = 'https://customer-assets.emergentagent.com/job_3f9e40e3-cca9-4507-83e7-20ced098354a/artifacts/bqpc8pkt_HERO%20IMAGE.png';

const PILLARS = [
  {
    title: 'Supporting Businesses',
    body: 'We help businesses of all sizes access secure and innovative payment technologies that enable growth, improve efficiency and support long-term success.',
  },
  {
    title: 'Communities & Partnerships',
    body: 'We recognise the importance of giving back and supporting the communities in which we operate. Through partnerships and charitable initiatives, we aim to make a positive contribution beyond payments.',
  },
  {
    title: 'Sustainability & Innovation',
    body: 'Digital technologies help reduce paper consumption and streamline operations. We continue to explore practical ways to improve efficiency and support more sustainable ways of doing business.',
  },
  {
    title: 'Our People',
    body: 'Our employees are at the heart of everything we do. We foster a culture built on collaboration, learning and professional development, empowering our teams to deliver exceptional experiences for our customers.',
  },
];

const COMMUNITY_FOCUS = [
  'Local community initiatives',
  'Charitable partnerships',
  'Financial literacy and education',
  'Supporting entrepreneurship and SMEs',
  'Employee volunteering and engagement',
];

const SUSTAINABILITY_AREAS = [
  'Digital-first documentation and reporting',
  'Reduced paper-based processes',
  'Cloud-based technologies and automation',
  'Efficient and scalable payment infrastructure',
  'Continuous innovation to simplify commerce',
];

const Eyebrow = ({ children }) => (
  <p className="text-[11px] font-bold text-[#ef5b25] uppercase tracking-[0.22em] mb-3">{children}</p>
);

const SectionHeading = ({ eyebrow, title, light = false }) => (
  <div className="mb-8">
    <Eyebrow>{eyebrow}</Eyebrow>
    <h2 className={`text-3xl sm:text-4xl lg:text-[2.75rem] font-bold leading-tight tracking-tight ${light ? 'text-white' : 'text-[#0a1929]'}`}>{title}</h2>
    <div className="h-1 w-20 bg-[#ef5b25] rounded-full mt-4" />
  </div>
);

/* Subtle scroll-reveal wrapper — fades + lifts element when it enters the viewport.
   `delay` is in ms and is applied as an inline transition-delay for staggered reveals. */
const Reveal = ({ children, delay = 0, className = '', as: Tag = 'div', ...rest }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -40px 0px' }
    );
    io.observe(node);
    return () => io.disconnect();
  }, []);

  return (
    <Tag
      ref={ref}
      style={{ transitionDelay: `${delay}ms` }}
      className={`reveal-up${visible ? ' is-visible' : ''} ${className}`}
      {...rest}
    >
      {children}
    </Tag>
  );
};

const AboutSocialResponsibility = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const fromTopNav = location.state?.from === 'top-nav';

  return (
    <div className="min-h-screen flex flex-col bg-white" data-testid="about-social-page">
      <Header />
      <div className="h-20" />

      {/* ╔═════════════════════ HERO ═════════════════════╗ */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#f4faf6] via-white to-[#eef3f8]" data-testid="social-hero">
        <div className="pointer-events-none absolute -top-32 -right-32 w-96 h-96 rounded-full bg-emerald-500/10 blur-3xl" aria-hidden="true" />
        <div className="pointer-events-none absolute -bottom-40 -left-32 w-96 h-96 rounded-full bg-[#1F4E79]/8 blur-3xl" aria-hidden="true" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-20 sm:pt-16 sm:pb-24">
          <button
            onClick={() => (fromTopNav ? navigate('/') : navigate(-1))}
            className="flex items-center gap-2 text-gray-600 hover:text-[#1F4E79] font-medium mb-10 transition-colors duration-200"
            data-testid="social-back-button"
          >
            <ArrowLeft size={18} />
            {fromTopNav ? 'Back to Home' : 'Back'}
          </button>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-gradient-to-r from-white/80 via-white/95 to-white/80 backdrop-blur-md border border-white/80 text-[#1F4E79] text-[11px] font-bold uppercase tracking-[0.22em] mb-6 shadow-[0_4px_20px_-2px_rgba(31,78,121,0.18)] ring-1 ring-inset ring-white/60">
                <Leaf size={13} className="text-emerald-500" strokeWidth={2} />
                Social Responsibility
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-[64px] font-bold text-[#0a1929] leading-[1.05] tracking-tight mb-6" data-testid="social-title">
                Building<br />
                <span className="text-[#1F4E79]">Responsible</span> Payment Technologies.
              </h1>
              <p className="text-lg sm:text-xl text-gray-600 leading-relaxed max-w-2xl mb-10 font-medium">
                At eCOMM, we believe success should create positive outcomes beyond transactions. We are committed to supporting our merchants, investing in our people and contributing to the communities we serve through responsible and sustainable business practices.
              </p>
              <div className="flex">
                <Link
                  to="/contact-sales"
                  onClick={() => window.__resetSourceScroll?.()}
                  className="group inline-flex items-center justify-center gap-2.5 px-7 py-3.5 bg-[#1F4E79] text-white font-bold rounded-lg hover:bg-[#163a5f] transition-all duration-200 shadow-md hover:shadow-lg"
                  data-testid="social-cta-approach"
                >
                  Talk to Our Team
                </Link>
              </div>
            </div>

            {/* Hero image card */}
            <div className="lg:col-span-5">
              <div className="relative max-w-md mx-auto lg:mx-0" data-testid="social-hero-image">
                <div className="absolute -inset-4 bg-gradient-to-br from-emerald-400/25 to-[#1F4E79]/15 rounded-3xl blur-2xl pointer-events-none" aria-hidden="true" />
                <div className="relative rounded-2xl overflow-hidden border border-white/60 shadow-2xl">
                  <img
                    src={HERO_IMAGE}
                    alt="ESG — Environmental, Social and Governance"
                    className="w-full h-auto object-cover aspect-square"
                  />
                  <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-[#0a1929]/90 via-[#0a1929]/55 to-transparent p-5 pt-14">
                    <p className="text-[10px] font-bold text-emerald-300 uppercase tracking-[0.2em] mb-1">Environmental · Social · Governance</p>
                    <p className="text-white font-bold text-xl leading-tight">Responsibility at the Core.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ╔═════════════════════ OUR APPROACH ═════════════════════╗ */}
      <section id="approach" className="bg-white py-20 sm:py-24" data-testid="social-approach">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <SectionHeading eyebrow="Our Approach" title="Creating value beyond payments." />
          </div>
          <div className="lg:col-span-7 text-gray-700 leading-relaxed text-base sm:text-lg space-y-5">
            <p>
              At eCOMM, we believe social responsibility is more than just technology. It is about the impact we create through the businesses we support, the relationships we build and the way we conduct ourselves every day.
            </p>
            <p>
              By helping merchants embrace digital commerce, investing in efficient technologies and fostering a culture built on integrity &amp; collaboration, we strive to create long-term value for our customers, employees, partners and communities. That is why we remain focused on delivering solutions that not only simplify payments, but also contribute positively to the wider ecosystem in which we operate.
            </p>
          </div>
        </div>
      </section>

      {/* ╔═════════════════════ FOUR PILLARS (plain, no icons or accents) ═════════════════════╗ */}
      <section className="bg-[#f7f9fc] py-20 sm:py-24" data-testid="social-pillars">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-12">
            <SectionHeading eyebrow="What We Stand For" title="Four pillars of social responsibility." />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {PILLARS.map((p, i) => (
              <Reveal
                key={i}
                delay={i * 110}
                className="rounded-xl bg-white border border-gray-100 shadow-sm hover:shadow-md transition-shadow duration-200 p-7 flex flex-col"
                data-testid={`pillar-card-${i}`}
              >
                <h3 className="font-bold text-[#0a1929] text-lg leading-snug mb-3">{p.title}</h3>
                <p className="text-[14px] text-gray-600 leading-relaxed">{p.body}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ╔═════════════════════ SUPPORTING COMMUNITIES (with image) ═════════════════════╗ */}
      <section className="bg-white py-20 sm:py-24" data-testid="supporting-communities">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          {/* Text on the LEFT */}
          <div className="lg:col-span-6 order-2 lg:order-1">
            <SectionHeading eyebrow="Supporting Communities" title="Strong communities create strong businesses." />
            <p className="text-lg text-gray-700 leading-relaxed mb-8">
              We believe in building lasting relationships and supporting initiatives that have a meaningful impact on society.
            </p>

            <p className="text-[11px] font-bold text-[#0a1929]/60 uppercase tracking-[0.2em] mb-4">Possible areas of focus</p>
            <ul className="space-y-3" data-testid="community-focus-list">
              {COMMUNITY_FOCUS.map((item, i) => (
                <li key={i} className="flex items-start gap-3" data-testid={`community-focus-${i}`}>
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center mt-0.5">
                    <Check size={12} className="text-emerald-600" strokeWidth={3} />
                  </span>
                  <span className="text-base text-gray-700 leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Image on the RIGHT */}
          <div className="lg:col-span-6 order-1 lg:order-2">
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-br from-emerald-300/20 to-[#1F4E79]/10 rounded-3xl blur-2xl pointer-events-none" aria-hidden="true" />
              <div className="relative rounded-2xl overflow-hidden border border-white/60 shadow-2xl">
                <img
                  src={COMMUNITY_IMAGE}
                  alt="Community — colourful figures arranged together"
                  className="w-full h-auto object-cover aspect-[4/3]"
                  data-testid="community-image"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ╔═════════════════════ SUSTAINABILITY ═════════════════════╗ */}
      <section className="bg-[#f7f9fc] py-20 sm:py-24" data-testid="social-sustainability">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <SectionHeading eyebrow="Sustainability" title="Technology that reduces complexity." />
            <p className="text-lg text-gray-700 leading-relaxed">
              While payments are inherently digital, we recognise the importance of operating responsibly and continuously improving efficiency.
            </p>
          </div>

          <div className="lg:col-span-7">
            <p className="text-[11px] font-bold text-[#0a1929]/60 uppercase tracking-[0.2em] mb-5">Key areas</p>
            <div className="rounded-2xl bg-white border border-gray-100 shadow-sm divide-y divide-gray-100 max-w-md" data-testid="sustainability-list">
              {SUSTAINABILITY_AREAS.map((item, i) => (
                <Reveal
                  key={i}
                  delay={i * 90}
                  className="flex items-center gap-4 px-6 py-5"
                  data-testid={`sustainability-item-${i}`}
                >
                  <span className="flex-shrink-0 w-7 h-7 rounded-full bg-[#1F4E79]/8 border border-[#1F4E79]/15 flex items-center justify-center text-[11px] font-bold text-[#1F4E79]">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <span className="text-base text-gray-800 leading-relaxed font-medium">{item}</span>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ╔═════════════════════ PEOPLE & CULTURE ═════════════════════╗ */}
      <section className="bg-white py-20 sm:py-24" data-testid="people-culture">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          {/* Image on LEFT */}
          <div className="lg:col-span-6 order-2 lg:order-1">
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-br from-[#1F4E79]/15 to-[#ef5b25]/15 rounded-3xl blur-2xl pointer-events-none" aria-hidden="true" />
              <div className="relative rounded-2xl overflow-hidden border border-white/60 shadow-2xl">
                <img
                  src={PEOPLE_IMAGE}
                  alt="eCOMM team collaborating around a laptop"
                  className="w-full h-auto object-cover aspect-[4/3]"
                  data-testid="people-image"
                />
              </div>
            </div>
          </div>

          {/* Text on RIGHT */}
          <div className="lg:col-span-6 order-1 lg:order-2">
            <SectionHeading eyebrow="People & Culture" title="Strong businesses are built together." />
            <p className="text-lg text-gray-700 leading-relaxed mb-8">
              Behind every payment is a team committed to innovation, collaboration and customer success. We foster an environment where ideas are encouraged, professional development is supported and everyone works towards a common goal — helping merchants succeed.
            </p>

            <p className="text-[11px] font-bold text-[#0a1929]/60 uppercase tracking-[0.2em] mb-4">Core Values</p>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-3" data-testid="people-values">
              {['Teamwork', 'Innovation', 'Integrity', 'Customer Focus'].map((v, i, arr) => (
                <React.Fragment key={v}>
                  <span className="text-base sm:text-lg font-bold text-[#1F4E79]">{v}</span>
                  {i < arr.length - 1 && <span className="text-[#ef5b25] text-lg leading-none">•</span>}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ╔═════════════════════ FINAL CTA / COMMITTED ═════════════════════╗ */}
      <section className="py-16 sm:py-20 bg-[#f7f9fc]" data-testid="social-final-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-2xl bg-gradient-to-br from-[#0a1929] via-[#0e2540] to-[#163a5f] p-8 sm:p-14 text-center shadow-xl relative overflow-hidden">
            <div className="pointer-events-none absolute -top-32 -right-32 w-96 h-96 rounded-full bg-emerald-400/15 blur-3xl" aria-hidden="true" />
            <div className="pointer-events-none absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-[#1F4E79]/30 blur-3xl" aria-hidden="true" />
            <div className="relative">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-400/15 border border-emerald-400/30 text-emerald-200 text-[11px] font-bold uppercase tracking-[0.22em] mb-5">
                <Sparkles size={12} />
                Committed To Responsible Growth
              </div>
              <h3 className="text-3xl sm:text-4xl lg:text-[2.5rem] font-bold text-white leading-tight tracking-tight mb-4">
                Creating a positive impact for the<br className="hidden sm:block" /> wider business community.
              </h3>
              <p className="text-white/90 text-base sm:text-lg leading-relaxed max-w-3xl mx-auto mb-8">
                At eCOMM, we want to create lasting value for merchants, employees, partners and communities. By combining innovation with responsible business practices, we aim to contribute positively to the people and businesses that rely on us every day.
              </p>
              <div className="flex justify-center">
                <Link
                  to="/contact-sales"
                  onClick={() => window.__resetSourceScroll?.()}
                  className="group inline-flex items-center justify-center gap-2.5 px-7 py-3.5 bg-white text-[#1F4E79] font-bold rounded-lg hover:bg-white/90 transition-all duration-200 shadow-md hover:shadow-lg"
                  data-testid="social-cta-final"
                >
                  Contact Us
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <BackToTop />
    </div>
  );
};

export default AboutSocialResponsibility;
